import { Component, input, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CatalogService } from '@services/data/catalog.service';
import { AppSettings } from '@services/settings/app-settings.service';

export type FlowBadgeSize = 'xs' | 's' | 'm' | 'l' | 'xl';
export type FlowBadgeDisplay = 'timeline' | 'badge' | 'auto';

export interface FlowStepItem {
  id: string;
  label: string;
  icon: string;
}

@Component({
  selector: 'app-flow-badge, app-status-timeline',
  standalone: true,
  imports: [CommonModule],
  host: {
    '[class.st-badge]': 'effectiveDisplay() === "badge"'
  },
  template: `
    @if (effectiveDisplay() === 'badge') {
      <span class="status-badge"
            [class.is-rejected]="isRejected()"
            [class.is-done]="isCurrentTerminal()"
            [title]="badgeTitle()">
        <i [class]="badgeIcon()"></i>
        <span>{{ currentLabel() }}</span>
        @if (timelineSteps().length > 1) {
          <span class="status-badge-count">{{ currentStepIndex() + 1 }}/{{ timelineSteps().length }}</span>
        }
      </span>
    } @else {
      <div class="status-timeline-container" [attr.data-size]="size()">
        <div class="timeline-wrapper">
          <div class="timeline-progress-bar" [style.left]="barInset()" [style.right]="barInset()">
            <div class="progress-fill" [style.width]="progressWidth()" [class.bg-danger]="isRejected()"></div>
          </div>

          <div class="d-flex position-relative">
            @for (step of timelineSteps(); track step.id; let i = $index) {
              <div class="timeline-step text-center"
                   [class.active]="i <= currentStepIndex()"
                   [class.current]="i === currentStepIndex()"
                   [class.rejected]="isRejected() && i === currentStepIndex()"
                   [title]="stepTitle(step, i)">
                <div class="step-icon-wrapper">
                  @if (isRejected() && i === currentStepIndex()) {
                    <i class="fas fa-times"></i>
                  } @else if (i < currentStepIndex() || (i === currentStepIndex() && isTerminal(step.id))) {
                    <i class="fas fa-check"></i>
                  } @else {
                    <i [class]="step.icon"></i>
                  }
                </div>
                <div class="step-label"
                     [class.text-danger]="isRejected() && i === currentStepIndex()"
                     [class.text-primary]="!isRejected() && i <= currentStepIndex()"
                     [class.text-muted]="i > currentStepIndex()">
                  {{ isRejected() && i === currentStepIndex() ? 'Rechazado' : step.label }}
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host { display: block; }
    :host(.st-badge) { display: inline-flex; }

    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.28rem 0.7rem;
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 600;
      line-height: 1.2;
      white-space: nowrap;
      background: rgba(var(--bs-primary-rgb, 13, 110, 253), 0.1);
      color: var(--bs-primary, #0d6efd);
      border: 1px solid rgba(var(--bs-primary-rgb, 13, 110, 253), 0.22);
    }
    .status-badge i { font-size: 0.7rem; }
    .status-badge-count {
      font-weight: 500;
      opacity: 0.7;
    }
    .status-badge.is-rejected {
      background: rgba(var(--bs-danger-rgb, 220, 53, 69), 0.1);
      color: var(--bs-danger, #dc3545);
      border-color: rgba(var(--bs-danger-rgb, 220, 53, 69), 0.25);
    }
    .status-badge.is-done {
      background: rgba(var(--bs-success-rgb, 25, 135, 84), 0.1);
      color: var(--bs-success, #198754);
      border-color: rgba(var(--bs-success-rgb, 25, 135, 84), 0.25);
    }

    .status-timeline-container {
      --st-icon: 28px;
      --st-font: 0.7rem;
      --st-line: 3px;
      --st-pad-y: 0.5rem;
      --st-pad-x: 0.75rem;
      --st-label-w: 5.5rem;
      padding: var(--st-pad-y) var(--st-pad-x);
    }

    .status-timeline-container[data-size='xs'] {
      --st-icon: 20px;
      --st-font: 0.6rem;
      --st-line: 2px;
      --st-pad-y: 0;
      --st-pad-x: 0;
      --st-label-w: 4.25rem;
    }
    .status-timeline-container[data-size='s'] {
      --st-icon: 26px;
      --st-font: 0.68rem;
      --st-line: 2px;
      --st-pad-y: 0.4rem;
      --st-pad-x: 0.5rem;
      --st-label-w: 5.25rem;
    }
    .status-timeline-container[data-size='m'] {
      --st-icon: 34px;
      --st-font: 0.75rem;
      --st-line: 3px;
      --st-pad-y: 0.6rem;
      --st-pad-x: 0.85rem;
      --st-label-w: 6.5rem;
    }
    .status-timeline-container[data-size='l'] {
      --st-icon: 44px;
      --st-font: 0.85rem;
      --st-line: 4px;
      --st-pad-y: 0.75rem;
      --st-pad-x: 1.25rem;
      --st-label-w: 7.5rem;
    }
    .status-timeline-container[data-size='xl'] {
      --st-icon: 52px;
      --st-font: 0.95rem;
      --st-line: 4px;
      --st-pad-y: 1rem;
      --st-pad-x: 1.5rem;
      --st-label-w: 8.5rem;
    }

    .timeline-wrapper {
      position: relative;
      width: fit-content;
      max-width: 100%;
      margin: 0 auto;
    }

    .timeline-progress-bar {
      position: absolute;
      top: calc(var(--st-icon) / 2);
      height: var(--st-line);
      background-color: var(--bs-border-color, #e9ecef);
      z-index: 1;
      border-radius: 99px;
      transform: translateY(-50%);
    }

    .progress-fill {
      height: 100%;
      background-color: var(--bs-primary, #0d6efd);
      transition: width 0.4s ease, background-color 0.4s ease;
      border-radius: 99px;
    }

    .timeline-step {
      position: relative;
      z-index: 2;
      flex: 0 0 auto;
      width: var(--st-label-w);
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .step-icon-wrapper {
      width: var(--st-icon);
      height: var(--st-icon);
      border-radius: 50%;
      background-color: var(--bs-secondary-bg, #e9ecef);
      color: var(--bs-secondary-color, #adb5bd);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: calc(var(--st-icon) * 0.42);
      border: 3px solid var(--bs-body-bg, #fff);
      transition: all 0.3s ease;
      flex-shrink: 0;
    }

    .timeline-step.active .step-icon-wrapper {
      background-color: var(--bs-primary, #0d6efd);
      color: #fff;
    }

    .timeline-step.current:not(.rejected) .step-icon-wrapper {
      box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb, 13, 110, 253), 0.18);
    }

    .timeline-step.rejected .step-icon-wrapper {
      background-color: var(--bs-danger, #dc3545);
      color: #fff;
      box-shadow: 0 0 0 3px rgba(var(--bs-danger-rgb, 220, 53, 69), 0.18);
    }

    .step-label {
      margin-top: 0.35rem;
      font-size: var(--st-font);
      font-weight: 600;
      letter-spacing: 0.2px;
      line-height: 1.2;
      max-width: var(--st-label-w);
      text-wrap: balance;
    }
  `]
})
export class FlowBadgeComponent {
  public appSettings = inject(AppSettings);
  processSlug = input<string | undefined | null>('');
  statusSlug = input<string | undefined | null>('');
  size = input<FlowBadgeSize>('s');
  display = input<FlowBadgeDisplay>('auto');

  // Determina la visualizacion segun configuracion y viewport movil
  effectiveDisplay = computed<FlowBadgeDisplay>(() => {
    const mode = this.display();
    return mode === 'auto' ? (this.appSettings.isMobile() ? 'badge' : 'timeline') : mode;
  });

  isRejected = computed(() => this.statusSlug() === 'REJECTED');

  private catalogService = inject(CatalogService);

  timelineSteps = computed<FlowStepItem[]>(() => {
    const slug = this.processSlug();
    const processConfig = this.catalogService.processes().find(p => p.slug === slug);

    if (processConfig?.statusFlow?.length) {
      const seen = new Set<string>();
      return [...processConfig.statusFlow]
        .filter((flow: any) => flow.isActive !== false)
        .sort((a: any, b: any) => (a.stepSequence ?? 0) - (b.stepSequence ?? 0))
        .reduce<FlowStepItem[]>((steps, flow: any) => {
          const id = flow.status?.slug;
          if (!id || seen.has(id)) return steps;
          seen.add(id);
          steps.push({
            id,
            label: flow.status?.name ?? id,
            icon: this.getIconForStatus(id)
          });
          return steps;
        }, []);
    }

    return [
      { id: 'PENDING', label: 'Ingresado', icon: 'fas fa-file-signature' },
      { id: 'APPROVED', label: 'Aprobado', icon: 'fas fa-check-double' }
    ];
  });

  // Retorna icono segun estado del catalogo
  private getIconForStatus(slug: string): string {
    const icons: Record<string, string> = {
      PENDING: 'fas fa-file-invoice',
      MINISTRY_APPROVED: 'fas fa-user-check',
      APPROVED: 'fas fa-check-double',
      PAID: 'fas fa-hand-holding-dollar',
      REVIEWING: 'fas fa-search',
      OBSERVED: 'fas fa-eye'
    };
    return icons[slug] ?? 'fas fa-dot-circle';
  }

  currentStepIndex = computed(() => {
    const status = this.statusSlug();
    const steps = this.timelineSteps();
    if (!status) return 0;

    if (status === 'REJECTED') {
      return steps.some(s => s.id === 'MINISTRY_APPROVED') ? 2 : 1;
    }

    const idx = steps.findIndex(s => s.id === status);
    return idx !== -1 ? idx : 0;
  });

  barInset = computed(() => {
    const n = this.timelineSteps().length;
    return n <= 1 ? '0%' : `${50 / n}%`;
  });

  progressWidth = computed(() => {
    const steps = this.timelineSteps();
    const current = this.currentStepIndex();
    return steps.length <= 1 ? '0%' : `${(current / (steps.length - 1)) * 100}%`;
  });

  // Evalua si el paso es terminal
  isTerminal(stepId: string): boolean {
    return stepId === 'PAID' || (stepId === 'APPROVED' && this.timelineSteps().length <= 2);
  }

  // Titulo descriptivo para accesibilidad
  stepTitle(step: FlowStepItem, index: number): string {
    return this.isRejected() && index === this.currentStepIndex() ? 'Rechazado' : step.label;
  }

  // Etiqueta para el badge
  currentLabel(): string {
    if (this.isRejected()) return 'Rechazado';
    return this.timelineSteps()[this.currentStepIndex()]?.label ?? 'Sin estado';
  }

  // Icono del badge de estado
  badgeIcon(): string {
    if (this.isRejected()) return 'fas fa-times';
    const step = this.timelineSteps()[this.currentStepIndex()];
    return step && this.isTerminal(step.id) ? 'fas fa-check' : (step?.icon ?? 'fas fa-dot-circle');
  }

  // Resumen del flujo para tooltip
  badgeTitle(): string {
    return this.timelineSteps().map(s => s.label).join(' → ');
  }

  // Indica si el estado actual es finalizado
  isCurrentTerminal(): boolean {
    const step = this.timelineSteps()[this.currentStepIndex()];
    return !!step && this.isTerminal(step.id) && !this.isRejected();
  }
}

export { FlowBadgeComponent as StatusTimelineComponent };
